import React, { useState, useCallback, useEffect } from "react";
import { confirmSignUp } from "aws-amplify/auth";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  OutlinedInput,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";


// Types
interface EmailConfirmProps {
  email: string;
  open: boolean;
  handleClose: () => void;
}

interface ConfirmationState {
  code: string;
  error: boolean;
  loading: boolean;
}

// Constants
const ERROR_MESSAGES = {
  CODE_MISMATCH: "Invalid verification code provided, please try again.",
  REQUIRED: "Confirmation code is required",
  GENERAL: "An error occurred while confirming your email. Please try again.",
} as const;

const INITIAL_STATE: ConfirmationState = {
  code: "",
  error: false,
  loading: false,
};

export const EmailConfirm: React.FC<EmailConfirmProps> = ({
  email,
  open,
  handleClose,
}) => {
  const [state, setState] = useState<ConfirmationState>(INITIAL_STATE);
  const navigate = useNavigate();

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setState(INITIAL_STATE);
    }
  }, [open]);

  const handleCodeChange = useCallback((value: string) => {
    setState(prev => ({
      ...prev,
      code: value,
      error: false, // Clear error when user types
    }));
  }, []);

  const handleConfirmation = useCallback(async () => {
    if (!state.code.trim()) {
      setState(prev => ({ ...prev, error: true }));
      return;
    }

    setState(prev => ({ ...prev, loading: true, error: false }));

    try {
      await confirmSignUp({
        username: email,
        confirmationCode: state.code.trim(),
      });
      handleClose();
    } catch (error: any) {
      setState(prev => ({
        ...prev,
        loading: false,
        error: true,
      }));

      // Log error for debugging
      console.error("Email confirmation error:", error);
    }
  }, [email, state.code, handleClose]);

  const handleDialogClose = useCallback(() => {
    if (!state.loading) {
      setState(INITIAL_STATE);
      navigate('/signIn', {
        replace: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.loading]);

  const handleKeyPress = useCallback((event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !state.loading) {
      event.preventDefault();
      handleConfirmation();
    }
  }, [handleConfirmation, state.loading]);

  return (
    <Dialog
      sx={{ m: 2 }}
      fullWidth
      maxWidth="sm"
      open={open}
      onClose={handleDialogClose}
      disableEscapeKeyDown={state.loading}
    >
      <DialogTitle sx={{ my: 2 }}>
        <Typography sx={{ color: "neutral" }} variant="subtitle1">
          Confirm email address
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Please enter the confirmation code sent to <strong>{email}</strong>
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth error={state.error}>
              <InputLabel shrink={false}>Confirmation Code*</InputLabel>
              <OutlinedInput
                name="confirmCode"
                value={state.code}
                onChange={(e) => handleCodeChange(e.target.value)}
                onKeyPress={handleKeyPress}
                type="text"
                required
                disabled={state.loading}
                autoFocus
                placeholder="Enter 6-digit code"
                inputProps={{
                  maxLength: 6,
                  pattern: "[0-9]*",
                  inputMode: "numeric",
                }}
              />
              {state.error && !state.code.trim() && (
                <FormHelperText error>
                  {ERROR_MESSAGES.REQUIRED}
                </FormHelperText>
              )}
            </FormControl>
          </Grid>

          {state.error && state.code.trim() && (
            <Grid item xs={12}>
              <Alert
                severity="error"
                onClose={() => setState(prev => ({ ...prev, error: false }))}
                sx={{ mt: 1 }}
              >
                {ERROR_MESSAGES.CODE_MISMATCH}
              </Alert>
            </Grid>
          )}

          <Grid item xs={12}>
            <Box sx={{ display: "flex", flexDirection: "row", pt: 2, gap: 1 }}>
              <Button
                variant="outlined"
                color="primary"
                size="large"
                onClick={handleDialogClose}
                disabled={state.loading}
              >
                Cancel
              </Button>

              <Box sx={{ flex: "1 1 auto" }} />

              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={handleConfirmation}
                disabled={state.loading || !state.code.trim()}
                type="submit"
              >
                {state.loading ? "Confirming..." : "Confirm"}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};
